/**
 * Detects Microsoft Dynamics RTM forms and strips inline styles
 * from their markup (including dynamically added elements).
 *
 * Optimized: initial full sweep per form, then scoped cleanups only.
 *
 * Now: removes *only* configured blacklisted CSS properties from inline styles,
 *      instead of removing the entire style attribute.
 *
 * Usage:
 *   import initRtmFormStyleStripper from './util/initRtmFormStyleStripper';
 *   initRtmFormStyleStripper(); // auto-init also runs once per page (race-safe)
 */

/* -------------------- config pickup (race-safe) -------------------- */

function _readInlineRtmConfig() {
    try {
        const el = document.getElementById('rtm-style-config');
        if (!el) return null;
        return JSON.parse(el.textContent);
    } catch (e) {
        console.warn('[RTM] failed to parse inline config', e);
        return null;
    }
}

function _getRtmConfigWithRaceSafety(timeoutMs = 300) {
    return new Promise((resolve) => {
        if (window.__pendingRtmStyleCfg) {
            resolve(window.__pendingRtmStyleCfg);
            return;
        }

        const now = _readInlineRtmConfig();
        if (now) { resolve(now); return; }

        let resolved = false;
        const finish = (cfg) => { if (!resolved) { resolved = true; resolve(cfg); } };

        const observer = new MutationObserver((muts, obs) => {
            const el = document.getElementById('rtm-style-config');
            if (el) {
                const cfg = _readInlineRtmConfig();
                obs.disconnect();
                finish(cfg || null);
            }
        });

        observer.observe(document.documentElement, { subtree: true, childList: true });

        setTimeout(() => {
            observer.disconnect();
            finish(_readInlineRtmConfig() || null);
        }, timeoutMs);
    });
}

/* -------------------- helper utils -------------------- */

function _escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Remove CSS declarations for provided property names from a style string.
// blacklists: array of property names in kebab-case (e.g. 'background', 'margin-top')
function _removeBlacklistedPropertiesFromStyle(styleString, blacklists) {
    if (!styleString || !styleString.trim()) return '';

    let newStyle = styleString;

    // Build regexes once for speed. Match property either at start or after semicolon, up to next semicolon.
    // Example regex for 'background-color': /(^|;)\s*background-color\s*:\s*[^;]+;?/gi
    for (const prop of blacklists) {
        if (!prop || typeof prop !== 'string') continue;
        const esc = _escapeRegExp(prop.trim());
        const rx = new RegExp(`(^|;)\\s*${esc}\\s*:\\s*[^;]+;?`, 'gi');
        newStyle = newStyle.replace(rx, '');
    }

    // Collapse sequential semicolons and whitespace, trim leading/trailing semicolons/whitespace
    newStyle = newStyle.replace(/;+/g, ';');            // collapse repeated semicolons
    newStyle = newStyle.replace(/^\s*;\s*/, '');         // leading semicolon
    newStyle = newStyle.replace(/\s*;\s*$/, '');         // trailing semicolon
    newStyle = newStyle.trim();

    return newStyle;
}

/* -------------------- exported initializer -------------------- */

export default function initRtmFormStyleStripper(options = {}) {
    // sensible JS defaults (will be overridden by server config if provided)
    const defaults = {
        enabled: true,
        debug: false,
        allowlist: [
            '[data-keep-style]',
            '.keep-inline-style',
            '.columnContainer',
            'select',
            'hr',
            '.g-recaptcha',
            '.grecaptcha-badge',
            '[data-sitekey]'
        ],
        // default blacklist focuses on backgrounds and spacing/alignment issues
        blacklist: [
            'background',
            'background-color',
            'background-image',
            'background-repeat',
            'background-position',
            'background-size',
            'margin',
            'margin-top',
            'margin-right',
            'margin-bottom',
            'margin-left',
            'padding',
            'padding-top',
            'padding-right',
            'padding-bottom',
            'padding-left',
            'float',
            'text-align'
        ]
    };

    // merge options onto defaults (options wins)
    const cfg = Object.assign({}, defaults, options || {});
    const debug = !!cfg.debug;
    const log = (...args) => { if (debug) console.log('[RTM StyleStripper]', ...args); };

    if (cfg.enabled === false) {
        log('disabled via config');
        return;
    }

    const RTM_CONTAINER_SELECTOR = '[data-form-api-url*="mkt.dynamics.com"][data-form-id]';
    const targetSelector = `${RTM_CONTAINER_SELECTOR} form`;

    // excludeSelectors come from cfg.allowlist (server can override)
    const excludeSelectors = Array.isArray(cfg.allowlist) ? cfg.allowlist.concat([]) : defaults.allowlist.slice();

    // blacklist properties
    const blacklistedProperties = Array.isArray(cfg.blacklist) ? cfg.blacklist.concat([]) : defaults.blacklist.slice();

    // prevent double init
    if (window.__rtmStyleStripperActive) {
        log('already active, skipping init');
        return;
    }
    window.__rtmStyleStripperActive = true;

    const matchesAny = (el, sels) => sels && sels.length && sels.some(s => {
        try { return el.matches(s); } catch (e) {
            if (debug) console.warn('[RTM] invalid selector in allowlist, ignoring:', s, e);
            return false;
        }
    });

    // Clean a single element by removing only blacklisted properties from its inline style
    const cleanElement = (el) => {
        if (!(el instanceof Element)) return;
        if (!el.hasAttribute('style')) return;

        if (matchesAny(el, excludeSelectors)) {
            log('element is allowlisted, skipping', el);
            return;
        }

        const raw = el.getAttribute('style') || '';
        const newStyle = _removeBlacklistedPropertiesFromStyle(raw, blacklistedProperties);

        if (newStyle !== raw) {
            if (newStyle === '') {
                el.removeAttribute('style');
                log('removed style attribute entirely (all props removed) from', el);
            } else {
                el.setAttribute('style', newStyle);
                log('updated style attribute on', el, 'before:', raw, 'after:', newStyle);
            }
        } else {
            // no change
            if (debug) log('no blacklisted props found on', el);
        }
    };

    // Clean a small subtree only (root + its [style] descendants)
    const stripSubtreeInlineStyles = (root) => {
        if (!(root instanceof Element)) return;
        if (matchesAny(root, excludeSelectors)) {
            log('skipping allowlisted root subtree:', root);
            return;
        }
        cleanElement(root);
        root.querySelectorAll('[style]').forEach(cleanElement);
    };

    // When a node changes, decide how much to clean
    const processIfFormContext = (node) => {
        if (!(node instanceof Element)) return;

        // Case 1: node IS a target form => full sweep of that form
        if (node.matches(targetSelector)) {
            log('form node matched, full sweep:', node);
            stripSubtreeInlineStyles(node);
            return;
        }

        // Case 2: node CONTAINS a target form => sweep nested form(s)
        const nested = node.querySelector?.(targetSelector);
        if (nested) {
            log('nested form detected, sweep:', nested);
            stripSubtreeInlineStyles(nested);
            return;
        }

        // Case 3: node is INSIDE a target form => scoped cleanup
        if (node.closest?.(targetSelector)) {
            log('node inside form, scoped cleanup:', node);
            stripSubtreeInlineStyles(node);
        }
    };

    const observeContainer = (container) => {
        log('Observing RTM container:', container);

        // Initial sweep for any already-present forms in this container
        container.querySelectorAll('form').forEach(form => {
            log('initial form sweep:', form);
            stripSubtreeInlineStyles(form);
        });

        const observer = new MutationObserver((muts) => {
            for (const m of muts) {
                if (m.type === 'childList') {
                    m.addedNodes.forEach((n) => processIfFormContext(n));
                } else if (m.type === 'attributes' && m.attributeName === 'style') {
                    const el = /** @type {Element} */ (m.target);
                    if (el.closest?.(targetSelector)) {
                        log('style attribute changed on element inside form:', el);
                        // scoped: only clean the changed element
                        cleanElement(el);
                    }
                }
            }
        });

        observer.observe(container, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['style']
        });
    };

    const bindIfPresent = () => {
        const containers = document.querySelectorAll(RTM_CONTAINER_SELECTOR);
        if (!containers.length) return false;
        containers.forEach(observeContainer);
        return true;
    };

    // do the work
    if (bindIfPresent()) {
        log('bound to existing containers');
        return;
    }

    // Bootstrap observer: watch for the RTM container(s) to appear, then bind and disconnect
    const bootstrap = new MutationObserver(() => {
        if (bindIfPresent()) {
            log('bootstrap found container, disconnecting bootstrap observer');
            bootstrap.disconnect();
        }
    });
    bootstrap.observe(document.documentElement, { subtree: true, childList: true });
}

/* -------------------- auto-init (race-safe) -------------------- */

if (!window.__rtmStyleStripperAutoInitRegistered) {
    window.__rtmStyleStripperAutoInitRegistered = true;

    const runWithConfig = () => {
        _getRtmConfigWithRaceSafety(300).then((serverCfg) => {
            const options = serverCfg || {};
            const queued = window.__pendingRtmStyleCfg || null;
            const merged = Object.assign({}, options, queued || {});
            try {
                if (typeof window.initRtmFormStyleStripper === 'function') {
                    window.initRtmFormStyleStripper(merged);
                } else {
                    // call local default if available
                    try { if (typeof initRtmFormStyleStripper === 'function') initRtmFormStyleStripper(merged); } catch (e) { /* ignore */ }
                }
            } catch (e) {
                console.warn('[RTM] auto-init failed with config', e);
            }
        }).catch((err) => {
            console.warn('[RTM] failed to get config for auto-init', err);
            try { if (typeof initRtmFormStyleStripper === 'function') initRtmFormStyleStripper(); } catch (e) { /* ignore */ }
        });
    };

    const run = () => {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', runWithConfig, { once: true });
        } else {
            runWithConfig();
        }
    };

    run();
}
